import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"
import SearchIcon from "@mui/icons-material/Search"
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined"

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import * as hrDepartmentService from "../../services/hrDepartmentService"


// =========================================================
// Service helpers
// =========================================================

const getFunction = (...names) => {
  for (const name of names) {
    if (
      typeof hrDepartmentService[name]
      === "function"
    ) {
      return hrDepartmentService[name]
    }
  }

  return null
}


const getDepartmentsLoader = () =>
  getFunction(
    "getDepartments",
    "getHrDepartments",
    "listDepartments",
    "getDepartmentList"
  )


const getPositionsLoader = () =>
  getFunction(
    "getPositions",
    "getJobPositions",
    "getHrJobPositions",
    "getHrPositions",
    "listPositions"
  )


const getFormDataLoader = () =>
  getFunction(
    "getFormData",
    "getDepartmentFormData",
    "getHrDepartmentFormData"
  )


const getCreateDepartment = () =>
  getFunction(
    "createDepartment",
    "createHrDepartment",
    "addDepartment"
  )


const getUpdateDepartment = () =>
  getFunction(
    "updateDepartment",
    "updateHrDepartment",
    "editDepartment"
  )


const getDepartmentStatusUpdater = () =>
  getFunction(
    "updateDepartmentStatus",
    "updateHrDepartmentStatus",
    "changeDepartmentStatus",
    "setDepartmentStatus"
  )


const getCreatePosition = () =>
  getFunction(
    "createPosition",
    "createJobPosition",
    "createHrJobPosition",
    "addPosition"
  )


const getUpdatePosition = () =>
  getFunction(
    "updatePosition",
    "updateJobPosition",
    "updateHrJobPosition",
    "editPosition"
  )


const getPositionStatusUpdater = () =>
  getFunction(
    "updatePositionStatus",
    "updateHrJobPositionStatus",
    "changePositionStatus",
    "setPositionStatus"
  )


// =========================================================
// Utils
// =========================================================

const extractItems = (result) => {
  if (Array.isArray(result)) {
    return result
  }

  return (
    result?.items ||
    result?.data?.items ||
    result?.data ||
    []
  )
}


const getApiError = (
  error,
  fallback
) => {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  return (
    detail?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  )
}


const employeeLabel = (
  employee
) => {
  if (!employee) {
    return "-"
  }

  const name = [
    employee.first_name,
    employee.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim()

  if (
    employee.employee_code &&
    name
  ) {
    return (
      `${employee.employee_code} - ${name}`
    )
  }

  return (
    name ||
    employee.employee_code ||
    `Employé #${employee.id}`
  )
}


const money = (
  value,
  currency = "USD"
) => {
  const number =
    Number(value || 0)

  return `${number.toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )} ${currency || "USD"}`
}


// =========================================================
// Initial states
// =========================================================

const emptyDepartmentForm = {
  id: null,
  code: "",
  name: "",
  description: "",
  parent_department_id: "",
  manager_employee_id: "",
  cost_center_code: "",
  display_order: 0,
  notes: "",
  is_active: true,
}


const emptyPositionForm = {
  id: null,
  department_id: "",
  code: "",
  name: "",
  description: "",
  reports_to_position_id: "",
  level_code: "",
  employment_category: "",
  min_salary: 0,
  max_salary: 0,
  currency: "USD",
  display_order: 0,
  notes: "",
  is_active: true,
}


// =========================================================
// KPI card
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        minHeight: 118,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            variant="h4"
            fontWeight={900}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// Page
// =========================================================

export default function DepartmentsPositionsPage() {
  const [
    tab,
    setTab,
  ] = useState(0)

  const [
    departments,
    setDepartments,
  ] = useState([])

  const [
    positions,
    setPositions,
  ] = useState([])

  const [
    employees,
    setEmployees,
  ] = useState([])

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    error,
    setError,
  ] = useState("")

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    departmentDialog,
    setDepartmentDialog,
  ] = useState(false)

  const [
    positionDialog,
    setPositionDialog,
  ] = useState(false)

  const [
    departmentForm,
    setDepartmentForm,
  ] = useState(
    emptyDepartmentForm
  )

  const [
    positionForm,
    setPositionForm,
  ] = useState(
    emptyPositionForm
  )

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    message: "",
    severity: "success",
  })


  // =======================================================
  // Snackbar
  // =======================================================

  const notify = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }


  // =======================================================
  // Load data
  // =======================================================

  const loadData = useCallback(
    async () => {
      setLoading(true)
      setError("")

      try {
        const departmentLoader =
          getDepartmentsLoader()

        const positionLoader =
          getPositionsLoader()

        const formDataLoader =
          getFormDataLoader()

        const requests = [
          departmentLoader
            ? departmentLoader()
            : Promise.resolve({
                items: [],
              }),

          positionLoader
            ? positionLoader()
            : Promise.resolve({
                items: [],
              }),

          formDataLoader
            ? formDataLoader()
            : Promise.resolve({}),
        ]

        const [
          departmentResult,
          positionResult,
          formDataResult,
        ] = await Promise.all(
          requests
        )

        const loadedDepartments =
          extractItems(
            departmentResult
          )

        const loadedPositions =
          extractItems(
            positionResult
          )

        setDepartments(
          Array.isArray(
            loadedDepartments
          )
            ? loadedDepartments
            : []
        )

        setPositions(
          Array.isArray(
            loadedPositions
          )
            ? loadedPositions
            : []
        )

        const formData =
          formDataResult?.data ||
          formDataResult ||
          {}

        setEmployees(
          Array.isArray(
            formData.employees
          )
            ? formData.employees
            : []
        )

        if (
          !positionLoader &&
          Array.isArray(
            formData.positions
          )
        ) {
          setPositions(
            formData.positions
          )
        }

      } catch (err) {
        setError(
          getApiError(
            err,
            "Impossible de charger la structure RH."
          )
        )
      } finally {
        setLoading(false)
      }
    },
    []
  )


  useEffect(() => {
    loadData()
  }, [loadData])


  // =======================================================
  // KPI
  // =======================================================

  const statistics =
    useMemo(
      () => ({
        departments:
          departments.length,

        activeDepartments:
          departments.filter(
            (item) =>
              item.is_active !== false
          ).length,

        positions:
          positions.length,

        activePositions:
          positions.filter(
            (item) =>
              item.is_active !== false
          ).length,

        employees:
          departments.reduce(
            (
              total,
              item
            ) =>
              total +
              Number(
                item.employee_count ||
                0
              ),
            0
          ),
      }),
      [
        departments,
        positions,
      ]
    )


  // =======================================================
  // Filters
  // =======================================================

  const filteredDepartments =
    useMemo(
      () => {
        const query =
          search
            .trim()
            .toLowerCase()

        if (!query) {
          return departments
        }

        return departments.filter(
          (item) =>
            [
              item.code,
              item.name,
              item.description,
              item.manager_name,
              item.parent_department_name,
            ]
              .filter(Boolean)
              .some(
                (value) =>
                  String(value)
                    .toLowerCase()
                    .includes(query)
              )
        )
      },
      [
        departments,
        search,
      ]
    )


  const filteredPositions =
    useMemo(
      () => {
        const query =
          search
            .trim()
            .toLowerCase()

        if (!query) {
          return positions
        }

        return positions.filter(
          (item) =>
            [
              item.code,
              item.name,
              item.department_name,
              item.description,
              item.level_code,
            ]
              .filter(Boolean)
              .some(
                (value) =>
                  String(value)
                    .toLowerCase()
                    .includes(query)
              )
        )
      },
      [
        positions,
        search,
      ]
    )


  // =======================================================
  // Department dialog
  // =======================================================

  const openCreateDepartment = () => {
    setDepartmentForm({
      ...emptyDepartmentForm,
    })

    setDepartmentDialog(true)
  }


  const openEditDepartment = (
    row
  ) => {
    setDepartmentForm({
      id:
        row.id,

      code:
        row.code || "",

      name:
        row.name || "",

      description:
        row.description || "",

      parent_department_id:
        row.parent_department_id ||
        "",

      manager_employee_id:
        row.manager_employee_id ||
        "",

      cost_center_code:
        row.cost_center_code || "",

      display_order:
        row.display_order || 0,

      notes:
        row.notes || "",

      is_active:
        row.is_active !== false,
    })

    setDepartmentDialog(true)
  }


  // =======================================================
  // Position dialog
  // =======================================================

  const openCreatePosition = () => {
    if (
      !departments.length
    ) {
      notify(
        "Créez d'abord au moins un département.",
        "warning"
      )

      return
    }

    setPositionForm({
      ...emptyPositionForm,
    })

    setPositionDialog(true)
  }


  const openEditPosition = (
    row
  ) => {
    setPositionForm({
      id:
        row.id,

      department_id:
        row.department_id || "",

      code:
        row.code || "",

      name:
        row.name || "",

      description:
        row.description || "",

      reports_to_position_id:
        row.reports_to_position_id ||
        "",

      level_code:
        row.level_code || "",

      employment_category:
        row.employment_category ||
        "",

      min_salary:
        row.min_salary || 0,

      max_salary:
        row.max_salary || 0,

      currency:
        row.currency || "USD",

      display_order:
        row.display_order || 0,

      notes:
        row.notes || "",

      is_active:
        row.is_active !== false,
    })

    setPositionDialog(true)
  }


  // =======================================================
  // Save department
  // =======================================================

  const saveDepartment =
    async () => {
      if (
        !departmentForm.name
          .trim()
      ) {
        notify(
          "Le nom du département est obligatoire.",
          "warning"
        )

        return
      }

      const isEdit =
        Boolean(
          departmentForm.id
        )

      const action =
        isEdit
          ? getUpdateDepartment()
          : getCreateDepartment()

      if (!action) {
        notify(
          "Le service de création/modification des départements n'est pas configuré.",
          "error"
        )

        return
      }

      setSaving(true)

      try {
        // IMPORTANT :
        // aucun code département
        // n'est envoyé au backend.
        const payload = {
          name:
            departmentForm.name
              .trim(),

          description:
            departmentForm.description
              ?.trim() ||
            null,

          parent_department_id:
            departmentForm
              .parent_department_id
              ? Number(
                  departmentForm
                    .parent_department_id
                )
              : null,

          manager_employee_id:
            departmentForm
              .manager_employee_id
              ? Number(
                  departmentForm
                    .manager_employee_id
                )
              : null,

          cost_center_code:
            departmentForm
              .cost_center_code
              ?.trim() ||
            null,

          display_order:
            Number(
              departmentForm
                .display_order ||
              0
            ),

          notes:
            departmentForm.notes
              ?.trim() ||
            null,

          is_active:
            departmentForm.is_active,
        }

        let result

        if (isEdit) {
          result = await action(
            departmentForm.id,
            payload
          )
        } else {
          result = await action(
            payload
          )
        }

        setDepartmentDialog(
          false
        )

        notify(
          isEdit
            ? "Département modifié avec succès."
            : (
                result?.code
                  ? `Département créé avec succès : ${result.code}`
                  : "Département créé avec succès."
              )
        )

        await loadData()

      } catch (err) {
        notify(
          getApiError(
            err,
            "Impossible d'enregistrer le département."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // Save position
  // =======================================================

  const savePosition =
    async () => {
      if (
        !positionForm.department_id
      ) {
        notify(
          "Sélectionnez un département.",
          "warning"
        )

        return
      }

      if (
        !positionForm.name
          .trim()
      ) {
        notify(
          "Le nom du poste est obligatoire.",
          "warning"
        )

        return
      }

      const minSalary =
        Number(
          positionForm.min_salary ||
          0
        )

      const maxSalary =
        Number(
          positionForm.max_salary ||
          0
        )

      if (
        maxSalary > 0 &&
        maxSalary < minSalary
      ) {
        notify(
          "Le salaire maximum doit être supérieur ou égal au salaire minimum.",
          "warning"
        )

        return
      }

      const isEdit =
        Boolean(
          positionForm.id
        )

      const action =
        isEdit
          ? getUpdatePosition()
          : getCreatePosition()

      if (!action) {
        notify(
          "Le service de création/modification des postes n'est pas configuré.",
          "error"
        )

        return
      }

      setSaving(true)

      try {
        // IMPORTANT :
        // aucun code poste n'est envoyé au backend.
        // Le backend le génère à la création et le conserve
        // ensuite comme identifiant technique immuable.
        const payload = {
          department_id:
            Number(
              positionForm
                .department_id
            ),

          name:
            positionForm.name
              .trim(),

          description:
            positionForm.description
              ?.trim() ||
            null,

          reports_to_position_id:
            positionForm
              .reports_to_position_id
              ? Number(
                  positionForm
                    .reports_to_position_id
                )
              : null,

          level_code:
            positionForm.level_code
              ?.trim() ||
            null,

          employment_category:
            positionForm
              .employment_category
              ?.trim() ||
            null,

          min_salary:
            minSalary,

          max_salary:
            maxSalary,

          currency:
            positionForm.currency ||
            "USD",

          display_order:
            Number(
              positionForm
                .display_order ||
              0
            ),

          notes:
            positionForm.notes
              ?.trim() ||
            null,

          is_active:
            positionForm.is_active,
        }

        let result

        if (isEdit) {
          result = await action(
            positionForm.id,
            payload
          )
        } else {
          result = await action(
            payload
          )
        }

        setPositionDialog(false)

        notify(
          isEdit
            ? "Poste modifié avec succès."
            : (
                result?.code
                  ? `Poste créé avec succès : ${result.code}`
                  : "Poste créé avec succès."
              )
        )

        await loadData()

      } catch (err) {
        notify(
          getApiError(
            err,
            "Impossible d'enregistrer le poste."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // Change department status
  // =======================================================

  const toggleDepartmentStatus =
    async (row) => {
      const action =
        getDepartmentStatusUpdater()

      if (!action) {
        notify(
          "Le service de changement de statut du département n'est pas configuré.",
          "error"
        )

        return
      }

      try {
        await action(
          row.id,
          {
            is_active:
              row.is_active ===
              false,
          }
        )

        notify(
          row.is_active === false
            ? "Département activé."
            : "Département désactivé."
        )

        await loadData()

      } catch (err) {
        notify(
          getApiError(
            err,
            "Impossible de modifier le statut."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // Change position status
  // =======================================================

  const togglePositionStatus =
    async (row) => {
      const action =
        getPositionStatusUpdater()

      if (!action) {
        notify(
          "Le service de changement de statut du poste n'est pas configuré.",
          "error"
        )

        return
      }

      try {
        await action(
          row.id,
          {
            is_active:
              row.is_active ===
              false,
          }
        )

        notify(
          row.is_active === false
            ? "Poste activé."
            : "Poste désactivé."
        )

        await loadData()

      } catch (err) {
        notify(
          getApiError(
            err,
            "Impossible de modifier le statut."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // Department columns
  // =======================================================

  const departmentColumns = [
    {
      field:
        "code",

      headerName:
        "Code",

      minWidth:
        125,
    },

    {
      field:
        "name",

      headerName:
        "Département",

      minWidth:
        190,

      flex:
        1,
    },

    {
      field:
        "parent_department_name",

      headerName:
        "Rattaché à",

      minWidth:
        170,

      renderCell:
        (params) =>
          params.value || "-",
    },

    {
      field:
        "manager_name",

      headerName:
        "Responsable",

      minWidth:
        190,

      flex:
        1,

      renderCell:
        (params) =>
          params.value || "-",
    },

    {
      field:
        "employee_count",

      headerName:
        "Employés",

      type:
        "number",

      minWidth:
        100,
    },

    {
      field:
        "position_count",

      headerName:
        "Postes",

      type:
        "number",

      minWidth:
        90,
    },

    {
      field:
        "is_active",

      headerName:
        "Statut",

      minWidth:
        110,

      renderCell:
        (params) => (
          <Chip
            size="small"
            label={
              params.value === false
                ? "Inactif"
                : "Actif"
            }
            color={
              params.value === false
                ? "default"
                : "success"
            }
          />
        ),
    },

    {
      field:
        "actions",

      headerName:
        "Actions",

      sortable:
        false,

      filterable:
        false,

      minWidth:
        240,

      renderCell:
        (params) => (
          <Stack
            direction="row"
            spacing={0.75}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={
                <EditOutlinedIcon />
              }
              onClick={() =>
                openEditDepartment(
                  params.row
                )
              }
            >
              Modifier
            </Button>

            <Button
              size="small"
              color={
                params.row
                  .is_active === false
                  ? "success"
                  : "warning"
              }
              onClick={() =>
                toggleDepartmentStatus(
                  params.row
                )
              }
            >
              {
                params.row
                  .is_active === false
                  ? "Activer"
                  : "Désactiver"
              }
            </Button>
          </Stack>
        ),
    },
  ]


  // =======================================================
  // Position columns
  // =======================================================

  const positionColumns = [
    {
      field:
        "code",

      headerName:
        "Code",

      minWidth:
        105,
    },

    {
      field:
        "name",

      headerName:
        "Poste",

      minWidth:
        190,

      flex:
        1,
    },

    {
      field:
        "department_name",

      headerName:
        "Département",

      minWidth:
        180,

      flex:
        1,
    },

    {
      field:
        "reports_to_position_name",

      headerName:
        "Supérieur",

      minWidth:
        170,

      renderCell:
        (params) =>
          params.value || "-",
    },

    {
      field:
        "employee_count",

      headerName:
        "Employés",

      type:
        "number",

      minWidth:
        100,
    },

    {
      field:
        "salary_range",

      headerName:
        "Fourchette salariale",

      minWidth:
        190,

      renderCell:
        (params) => {
          const row =
            params.row

          const min =
            Number(
              row.min_salary ||
              0
            )

          const max =
            Number(
              row.max_salary ||
              0
            )

          if (
            min === 0 &&
            max === 0
          ) {
            return "-"
          }

          if (
            max === 0
          ) {
            return money(
              min,
              row.currency
            )
          }

          return `${money(
            min,
            row.currency
          )} – ${money(
            max,
            row.currency
          )}`
        },
    },

    {
      field:
        "is_active",

      headerName:
        "Statut",

      minWidth:
        110,

      renderCell:
        (params) => (
          <Chip
            size="small"
            label={
              params.value === false
                ? "Inactif"
                : "Actif"
            }
            color={
              params.value === false
                ? "default"
                : "success"
            }
          />
        ),
    },

    {
      field:
        "actions",

      headerName:
        "Actions",

      sortable:
        false,

      filterable:
        false,

      minWidth:
        240,

      renderCell:
        (params) => (
          <Stack
            direction="row"
            spacing={0.75}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={
                <EditOutlinedIcon />
              }
              onClick={() =>
                openEditPosition(
                  params.row
                )
              }
            >
              Modifier
            </Button>

            <Button
              size="small"
              color={
                params.row
                  .is_active === false
                  ? "success"
                  : "warning"
              }
              onClick={() =>
                togglePositionStatus(
                  params.row
                )
              }
            >
              {
                params.row
                  .is_active === false
                  ? "Activer"
                  : "Désactiver"
              }
            </Button>
          </Stack>
        ),
    },
  ]


  return (
    <MainLayout>
      <Box
        sx={{
          p: {
            xs: 1.25,
            sm: 2,
            md: 3,
          },
        }}
      >
        {/* ===============================================
            HEADER
        =============================================== */}

        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          spacing={2}
          mb={2}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2.125rem",
                },
              }}
            >
              Départements & postes
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              L'entreprise construit librement sa structure organisationnelle et ses fonctions.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={
                <RefreshIcon />
              }
              onClick={
                loadData
              }
              disabled={
                loading
              }
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              startIcon={
                <BusinessOutlinedIcon />
              }
              onClick={
                openCreateDepartment
              }
            >
              Nouveau département
            </Button>

            <Button
              variant="contained"
              color="secondary"
              startIcon={
                <WorkOutlineOutlinedIcon />
              }
              onClick={
                openCreatePosition
              }
            >
              Nouveau poste
            </Button>
          </Stack>
        </Stack>


        {/* ===============================================
            INFORMATION
        =============================================== */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
          }}
        >
          Aucun département n'est imposé par RLSys.
          Chaque entreprise crée sa propre organisation.
          Les codes techniques des départements et des postes sont générés automatiquement par le système.
        </Alert>


        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
            }}
          >
            {error}
          </Alert>
        )}


        {/* ===============================================
            KPI
        =============================================== */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs:
                "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              xl:
                "repeat(4, minmax(0, 1fr))",
            },

            gap:
              2,

            mb:
              2,
          }}
        >
          <KpiCard
            title="Départements"
            value={
              statistics.departments
            }
            subtitle={
              `${statistics.activeDepartments} actif(s)`
            }
            icon={
              <BusinessOutlinedIcon />
            }
          />

          <KpiCard
            title="Postes"
            value={
              statistics.positions
            }
            subtitle={
              `${statistics.activePositions} actif(s)`
            }
            icon={
              <WorkOutlineOutlinedIcon />
            }
          />

          <KpiCard
            title="Employés affectés"
            value={
              statistics.employees
            }
            subtitle="Selon les départements"
            icon={
              <AccountTreeOutlinedIcon />
            }
          />

          <KpiCard
            title="Organisation"
            value={
              departments.length
                ? "Configurée"
                : "À créer"
            }
            subtitle="Département → Poste → Employé"
            icon={
              <AccountTreeOutlinedIcon />
            }
          />
        </Box>


        {/* ===============================================
            TABLES
        =============================================== */}

        <Paper
          elevation={0}
          sx={{
            border:
              "1px solid",

            borderColor:
              "divider",

            borderRadius:
              3,

            overflow:
              "hidden",
          }}
        >
          <Box
            sx={{
              px:
                2,

              pt:
                1.5,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Tabs
              value={
                tab
              }
              onChange={
                (
                  _,
                  value
                ) =>
                  setTab(
                    value
                  )
              }
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                label={
                  `Départements (${departments.length})`
                }
              />

              <Tab
                label={
                  `Postes (${positions.length})`
                }
              />
            </Tabs>
          </Box>


          <Box
            sx={{
              p:
                2,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={
                search
              }
              onChange={
                (event) =>
                  setSearch(
                    event.target.value
                  )
              }
              placeholder={
                tab === 0
                  ? "Rechercher un département..."
                  : "Rechercher un poste..."
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>


          <Box
            sx={{
              width:
                "100%",

              height: {
                xs:
                  500,

                md:
                  560,
              },

              overflow:
                "auto",
            }}
          >
            <DataGrid
              rows={
                tab === 0
                  ? filteredDepartments
                  : filteredPositions
              }
              columns={
                tab === 0
                  ? departmentColumns
                  : positionColumns
              }
              loading={
                loading
              }
              disableRowSelectionOnClick
              pageSizeOptions={[
                5,
                10,
                25,
              ]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page:
                      0,

                    pageSize:
                      10,
                  },
                },
              }}
              sx={{
                border:
                  0,

                minWidth:
                  tab === 0
                    ? 1000
                    : 1200,

                "& .MuiDataGrid-columnHeaders":
                  {
                    bgcolor:
                      "action.hover",

                    fontWeight:
                      800,
                  },
              }}
            />
          </Box>
        </Paper>


        {/* ===============================================
            DEPARTMENT DIALOG
        =============================================== */}

        <Dialog
          open={
            departmentDialog
          }
          onClose={() =>
            !saving &&
            setDepartmentDialog(
              false
            )
          }
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius:
                2,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight:
                900,
            }}
          >
            {
              departmentForm.id
                ? "Modifier le département"
                : "Nouveau département"
            }
          </DialogTitle>

          <DialogContent
            dividers
          >
            {!departmentForm.id && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                }}
              >
                Le code du département sera généré automatiquement lors de la création.
                Exemple : DEP-00001.
              </Alert>
            )}

            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns: {
                  xs:
                    "1fr",

                  md:
                    "repeat(2, minmax(0, 1fr))",
                },

                gap:
                  2,
              }}
            >
              {departmentForm.id && (
                <TextField
                  label="Code système"
                  value={
                    departmentForm.code ||
                    "-"
                  }
                  disabled
                  helperText="Identifiant généré automatiquement et non modifiable."
                />
              )}


              <TextField
                required
                label="Nom du département"
                value={
                  departmentForm.name
                }
                onChange={
                  (event) =>
                    setDepartmentForm(
                      (current) => ({
                        ...current,

                        name:
                          event.target.value,
                      })
                    )
                }
                helperText={
                  departmentForm.id
                    ? "Le code système reste inchangé."
                    : "Saisissez uniquement le nom du département."
                }
                sx={{
                  gridColumn:
                    departmentForm.id
                      ? undefined
                      : {
                          md:
                            "1 / -1",
                        },
                }}
              />


              <TextField
                select
                label="Département parent"
                value={
                  departmentForm
                    .parent_department_id
                }
                onChange={
                  (event) =>
                    setDepartmentForm(
                      (current) => ({
                        ...current,

                        parent_department_id:
                          event.target.value,
                      })
                    )
                }
              >
                <MenuItem value="">
                  Aucun — département principal
                </MenuItem>

                {
                  departments
                    .filter(
                      (
                        department
                      ) =>
                        department.id !==
                        departmentForm.id
                    )
                    .map(
                      (
                        department
                      ) => (
                        <MenuItem
                          key={
                            department.id
                          }
                          value={
                            department.id
                          }
                        >
                          {
                            department.code
                          }{" "}
                          -{" "}
                          {
                            department.name
                          }
                        </MenuItem>
                      )
                    )
                }
              </TextField>


              <TextField
                select
                label="Responsable"
                value={
                  departmentForm
                    .manager_employee_id
                }
                onChange={
                  (event) =>
                    setDepartmentForm(
                      (current) => ({
                        ...current,

                        manager_employee_id:
                          event.target.value,
                      })
                    )
                }
              >
                <MenuItem value="">
                  Aucun responsable
                </MenuItem>

                {
                  employees.map(
                    (
                      employee
                    ) => (
                      <MenuItem
                        key={
                          employee.id
                        }
                        value={
                          employee.id
                        }
                      >
                        {
                          employeeLabel(
                            employee
                          )
                        }
                      </MenuItem>
                    )
                  )
                }
              </TextField>


              <TextField
                label="Centre de coût"
                placeholder="Optionnel"
                value={
                  departmentForm
                    .cost_center_code
                }
                onChange={
                  (event) =>
                    setDepartmentForm(
                      (current) => ({
                        ...current,

                        cost_center_code:
                          event.target.value,
                      })
                    )
                }
              />


              <TextField
                type="number"
                label="Ordre d'affichage"
                value={
                  departmentForm
                    .display_order
                }
                onChange={
                  (event) =>
                    setDepartmentForm(
                      (current) => ({
                        ...current,

                        display_order:
                          event.target.value,
                      })
                    )
                }
                inputProps={{
                  min:
                    0,
                }}
              />


              <TextField
                label="Description"
                multiline
                minRows={3}
                value={
                  departmentForm
                    .description
                }
                onChange={
                  (event) =>
                    setDepartmentForm(
                      (current) => ({
                        ...current,

                        description:
                          event.target.value,
                      })
                    )
                }
                sx={{
                  gridColumn: {
                    md:
                      "1 / -1",
                  },
                }}
              />


              <TextField
                label="Notes"
                multiline
                minRows={2}
                value={
                  departmentForm
                    .notes
                }
                onChange={
                  (event) =>
                    setDepartmentForm(
                      (current) => ({
                        ...current,

                        notes:
                          event.target.value,
                      })
                    )
                }
                sx={{
                  gridColumn: {
                    md:
                      "1 / -1",
                  },
                }}
              />


              <FormControlLabel
                control={
                  <Switch
                    checked={
                      departmentForm
                        .is_active
                    }
                    onChange={
                      (event) =>
                        setDepartmentForm(
                          (
                            current
                          ) => ({
                            ...current,

                            is_active:
                              event.target.checked,
                          })
                        )
                    }
                  />
                }
                label="Département actif"
                sx={{
                  gridColumn: {
                    md:
                      "1 / -1",
                  },
                }}
              />
            </Box>
          </DialogContent>


          <DialogActions
            sx={{
              p:
                2,

              flexDirection: {
                xs:
                  "column-reverse",

                sm:
                  "row",
              },

              "& > :not(style) ~ :not(style)":
                {
                  ml: {
                    xs:
                      0,

                    sm:
                      1,
                  },

                  mt: {
                    xs:
                      1,

                    sm:
                      0,
                  },
                },
            }}
          >
            <Button
              onClick={() =>
                setDepartmentDialog(
                  false
                )
              }
              disabled={
                saving
              }
              sx={{
                width: {
                  xs:
                    "100%",

                  sm:
                    "auto",
                },
              }}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={
                saveDepartment
              }
              disabled={
                saving
              }
              startIcon={
                saving
                  ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  )
                  : (
                    <BusinessOutlinedIcon />
                  )
              }
              sx={{
                width: {
                  xs:
                    "100%",

                  sm:
                    "auto",
                },
              }}
            >
              {
                departmentForm.id
                  ? "Enregistrer"
                  : "Créer le département"
              }
            </Button>
          </DialogActions>
        </Dialog>


        {/* ===============================================
            POSITION DIALOG
        =============================================== */}

        <Dialog
          open={
            positionDialog
          }
          onClose={() =>
            !saving &&
            setPositionDialog(
              false
            )
          }
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius:
                2,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight:
                900,
            }}
          >
            {
              positionForm.id
                ? "Modifier le poste"
                : "Nouveau poste"
            }
          </DialogTitle>

          <DialogContent
            dividers
          >
            {!positionForm.id && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                }}
              >
                Le code du poste sera généré automatiquement lors de la création.
                Exemple : POS-00001.
              </Alert>
            )}

            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns: {
                  xs:
                    "1fr",

                  md:
                    "repeat(2, minmax(0, 1fr))",
                },

                gap:
                  2,

                pt:
                  0.5,
              }}
            >
              <TextField
                required
                select
                label="Département"
                value={
                  positionForm
                    .department_id
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        department_id:
                          event.target.value,
                      })
                    )
                }
              >
                {
                  departments
                    .filter(
                      (
                        department
                      ) =>
                        department.is_active !==
                        false
                    )
                    .map(
                      (
                        department
                      ) => (
                        <MenuItem
                          key={
                            department.id
                          }
                          value={
                            department.id
                          }
                        >
                          {
                            department.code
                          }{" "}
                          -{" "}
                          {
                            department.name
                          }
                        </MenuItem>
                      )
                    )
                }
              </TextField>


              {positionForm.id && (
                <TextField
                  label="Code système"
                  value={
                    positionForm.code ||
                    "-"
                  }
                  disabled
                  helperText="Identifiant généré automatiquement et non modifiable."
                />
              )}


              <TextField
                required
                label="Nom du poste"
                value={
                  positionForm.name
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        name:
                          event.target.value,
                      })
                    )
                }
                helperText={
                  positionForm.id
                    ? "Le code système reste inchangé."
                    : "Saisissez uniquement le nom du poste."
                }
                sx={{
                  gridColumn:
                    positionForm.id
                      ? undefined
                      : {
                          md:
                            "1 / -1",
                        },
                }}
              />


              <TextField
                select
                label="Poste supérieur"
                value={
                  positionForm
                    .reports_to_position_id
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        reports_to_position_id:
                          event.target.value,
                      })
                    )
                }
              >
                <MenuItem value="">
                  Aucun
                </MenuItem>

                {
                  positions
                    .filter(
                      (
                        position
                      ) =>
                        position.id !==
                        positionForm.id
                    )
                    .map(
                      (
                        position
                      ) => (
                        <MenuItem
                          key={
                            position.id
                          }
                          value={
                            position.id
                          }
                        >
                          {
                            position.code
                          }{" "}
                          -{" "}
                          {
                            position.name
                          }
                        </MenuItem>
                      )
                    )
                }
              </TextField>


              <TextField
                label="Niveau"
                placeholder="Ex. JUNIOR, SENIOR, MANAGER"
                value={
                  positionForm
                    .level_code
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        level_code:
                          event.target.value,
                      })
                    )
                }
              />


              <TextField
                label="Catégorie d'emploi"
                placeholder="Ex. Administration, Terrain..."
                value={
                  positionForm
                    .employment_category
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        employment_category:
                          event.target.value,
                      })
                    )
                }
              />


              <TextField
                type="number"
                label="Salaire minimum"
                value={
                  positionForm
                    .min_salary
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        min_salary:
                          event.target.value,
                      })
                    )
                }
                inputProps={{
                  min:
                    0,
                }}
              />


              <TextField
                type="number"
                label="Salaire maximum"
                value={
                  positionForm
                    .max_salary
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        max_salary:
                          event.target.value,
                      })
                    )
                }
                inputProps={{
                  min:
                    0,
                }}
              />


              <TextField
                select
                label="Devise"
                value={
                  positionForm.currency
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        currency:
                          event.target.value,
                      })
                    )
                }
              >
                <MenuItem value="USD">
                  USD
                </MenuItem>

                <MenuItem value="CDF">
                  CDF
                </MenuItem>
              </TextField>


              <TextField
                type="number"
                label="Ordre d'affichage"
                value={
                  positionForm
                    .display_order
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        display_order:
                          event.target.value,
                      })
                    )
                }
                inputProps={{
                  min:
                    0,
                }}
              />


              <TextField
                label="Description"
                multiline
                minRows={3}
                value={
                  positionForm
                    .description
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        description:
                          event.target.value,
                      })
                    )
                }
                sx={{
                  gridColumn: {
                    md:
                      "1 / -1",
                  },
                }}
              />


              <TextField
                label="Notes"
                multiline
                minRows={2}
                value={
                  positionForm
                    .notes
                }
                onChange={
                  (event) =>
                    setPositionForm(
                      (current) => ({
                        ...current,

                        notes:
                          event.target.value,
                      })
                    )
                }
                sx={{
                  gridColumn: {
                    md:
                      "1 / -1",
                  },
                }}
              />


              <FormControlLabel
                control={
                  <Switch
                    checked={
                      positionForm
                        .is_active
                    }
                    onChange={
                      (event) =>
                        setPositionForm(
                          (
                            current
                          ) => ({
                            ...current,

                            is_active:
                              event.target.checked,
                          })
                        )
                    }
                  />
                }
                label="Poste actif"
                sx={{
                  gridColumn: {
                    md:
                      "1 / -1",
                  },
                }}
              />
            </Box>
          </DialogContent>


          <DialogActions
            sx={{
              p:
                2,

              flexDirection: {
                xs:
                  "column-reverse",

                sm:
                  "row",
              },
            }}
          >
            <Button
              onClick={() =>
                setPositionDialog(
                  false
                )
              }
              disabled={
                saving
              }
              sx={{
                width: {
                  xs:
                    "100%",

                  sm:
                    "auto",
                },
              }}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={
                savePosition
              }
              disabled={
                saving
              }
              startIcon={
                saving
                  ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  )
                  : (
                    <WorkOutlineOutlinedIcon />
                  )
              }
              sx={{
                width: {
                  xs:
                    "100%",

                  sm:
                    "auto",
                },
              }}
            >
              {
                positionForm.id
                  ? "Enregistrer"
                  : "Créer le poste"
              }
            </Button>
          </DialogActions>
        </Dialog>


        {/* ===============================================
            SNACKBAR
        =============================================== */}

        <Snackbar
          open={
            snackbar.open
          }
          autoHideDuration={
            4000
          }
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,

                open:
                  false,
              })
            )
          }
          anchorOrigin={{
            vertical:
              "top",

            horizontal:
              "right",
          }}
        >
          <Alert
            severity={
              snackbar.severity
            }
            variant="filled"
            onClose={() =>
              setSnackbar(
                (current) => ({
                  ...current,

                  open:
                    false,
                })
              )
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}